import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useFilters } from '../../contexts/FilterContext';
import { Card, Spinner, ErrorBox, Empty } from '../../components/ui';
import MultiDropdown from '../../components/MultiDropdown';
import { useStoreOptions, SaveBar, useEditableList } from '../../components/adminKit';
import { money, addDays } from '../../lib/format';

// Accounting Profiles: the chart-of-accounts mapping the Sales Journal and
// Timesheets exports are built from (docs/accounting.md). A profile covers
// one or more stores; stores in no profile use the built-in default. Admins
// manage every profile; owners and bookkeepers only profiles built from
// their own stores (enforced server-side, like store groups).

const SOFTWARE = [
  { id: 'qbd', label: 'QuickBooks Desktop (IIF)' },
  { id: 'qbo', label: 'QuickBooks Online (CSV import)' },
  { id: 'csv', label: 'Other / generic CSV' },
];
const PAY_PERIODS = [
  { id: 'weekly', label: 'Weekly' },
  { id: 'biweekly', label: 'Every two weeks' },
  { id: 'semimonthly', label: 'Twice a month (1st & 16th)' },
  { id: 'monthly', label: 'Monthly' },
];
const ACCOUNT_LABELS = [
  ['sales', 'Sales income'],
  ['tax1', 'GST / HST payable'],
  ['tax2', 'PST / QST payable'],
  ['tax3', 'Liquor tax (LCT) payable'],
  ['tips', 'Tips payable'],
  ['giftCardLiability', 'Gift cards outstanding (liability)'],
  ['deliveryIncome', 'Delivery charges income'],
  ['discounts', 'Discounts given (contra-sales)'],
  ['refunds', 'Refunds (contra-sales)'],
  ['refundTender', 'Refunds paid from / undeposited funds'],
  ['storeCredit', 'Customer credits redeemed (liability)'],
  ['otherCharges', 'Charges applied after tax (deposits)'],
  ['overShort', 'Cash over / short'],
  ['houseAccountsAR', 'House accounts receivable'],
  ['costsDefault', 'Store expenses (default for costs)'],
];
const ORDER_TYPES = ['Pickup', 'Delivery', 'Dine In', 'Quick Dine In'];

const blankProfile = (dflt) => ({
  id: '',
  name: '',
  locations: [],
  software: 'csv',
  classes: {},
  accounts: { ...(dflt?.accounts || {}), salesByOrderType: null, costsByName: {} },
  tenders: { ...(dflt?.tenders || {}) },
  options: { ...(dflt?.options || {}) },
  payPeriod: { ...(dflt?.payPeriod || {}) },
  timesheets: { showPay: false, overtimeWeeklyHours: null, qbNames: {}, adp: { coCode: '', batchId: '', fileNumbers: {} } },
});

const nameHint = (software) => software === 'qbd'
  ? 'Names must match your QuickBooks Desktop chart of accounts exactly (sub-accounts as Parent:Child) — IIF import silently creates any name it does not find.'
  : software === 'qbo'
    ? 'Names must match your QuickBooks Online chart of accounts (sub-accounts as Parent:Child); turn account numbers off before importing.'
    : 'Account names are copied into the CSV as typed.';

// Tenders actually used by the profile's stores over the last ~90 days, so
// the mapping shows what matters and flags anything unmapped in red.
function TenderMap({ profile, patch, defaults, anchor, disabled }) {
  const [seen, setSeen] = useState(null);
  const [err, setErr] = useState(null);
  const locs = profile.locations.join(',');

  useEffect(() => {
    if (!locs) { setSeen([]); return; }
    let live = true;
    setSeen(null);
    axios.get('/api/reports/accounting/tenders', {
      params: { startDate: addDays(anchor, -89), endDate: anchor, locations: locs },
    })
      .then(r => { if (live) setSeen(Array.isArray(r.data) ? r.data : []); })
      .catch(e => { if (live) { setSeen([]); setErr(e.response?.data?.error || e.message); } });
    return () => { live = false; };
  }, [locs, anchor]);

  const tenders = profile.tenders || {};
  const rows = useMemo(() => {
    const names = new Set([...(seen || []).map(t => t.tender), ...Object.keys(tenders)]);
    const amounts = Object.fromEntries((seen || []).map(t => [t.tender, t]));
    return [...names].sort((a, b) => (amounts[b]?.amount || 0) - (amounts[a]?.amount || 0))
      .map(name => ({ name, amount: amounts[name]?.amount, payments: amounts[name]?.payments, account: tenders[name] || '' }));
  }, [seen, tenders]);
  const unmapped = rows.filter(r => !r.account).length;
  const [custom, setCustom] = useState('');

  const setTender = (name, account) => patch({ tenders: { ...tenders, [name]: account } });
  const fillDefaults = () => {
    const next = { ...tenders };
    for (const r of rows) if (!next[r.name] && defaults?.[r.name]) next[r.name] = defaults[r.name];
    patch({ tenders: next });
  };

  return (
    <div className="admin-section">
      <div className="admin-section-head">
        <strong>Tenders → deposit accounts</strong>
        {unmapped > 0
          ? <span className="pill bad">{unmapped} unmapped</span>
          : rows.length > 0 && <span className="pill good">all mapped</span>}
        <button className="btn-ghost" disabled={disabled} onClick={fillDefaults}>Fill from defaults</button>
      </div>
      <div className="hint">Where each payment type lands in your books. Unmapped tenders block the QuickBooks exports until you name an account.</div>
      {err && <div className="hint">Could not load recent tenders ({err}) — you can still map by name.</div>}
      {seen === null && <Spinner label="Looking up tenders used recently…" />}
      {seen !== null && rows.length === 0 && <Empty>No tenders seen for these stores yet. Add one by name below.</Empty>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="data admin-table">
            <thead>
              <tr>
                <th className="no-sort">Tender</th>
                <th className="no-sort num">Last 90 days</th>
                <th className="no-sort">Account</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.name} className={r.account ? '' : 'unmapped'}>
                  <td>{r.name}{!r.account && <span className="pill bad" style={{ marginLeft: 6 }}>unmapped</span>}</td>
                  <td className="num">{r.amount !== undefined ? `${money(r.amount)} · ${r.payments} pmts` : <span className="hint">not seen</span>}</td>
                  <td>
                    <input
                      className="field"
                      placeholder={defaults?.[r.name] || 'Account name'}
                      value={r.account}
                      onChange={e => setTender(r.name, e.target.value)}
                      style={{ minWidth: 220 }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="admin-addrow">
        <input className="field" placeholder="Add a tender by name (e.g. Uber Eats)" value={custom}
          onChange={e => setCustom(e.target.value)} onKeyDown={e => e.key === 'Enter' && custom.trim() && (setTender(custom.trim(), ''), setCustom(''))} />
        <button className="btn-ghost" disabled={disabled || !custom.trim()} onClick={() => { setTender(custom.trim(), ''); setCustom(''); }}>+ Add tender</button>
      </div>
    </div>
  );
}

// Payroll identifiers per employee (QuickBooks employee name, ADP file #),
// seeded from whoever clocked in at these stores over the last ~90 days.
function EmployeeMap({ profile, patch, anchor, disabled }) {
  const [emps, setEmps] = useState(null);
  const locs = profile.locations.join(',');
  useEffect(() => {
    if (!locs) { setEmps([]); return; }
    let live = true;
    setEmps(null);
    axios.get('/api/reports/accounting/timesheets', {
      params: { startDate: addDays(anchor, -89), endDate: anchor, locations: locs },
    })
      .then(r => {
        if (!live) return;
        const seen = new Map();
        for (const s of r.data?.stores || []) for (const e of s.employees || []) {
          if (!seen.has(e.employeeId)) seen.set(e.employeeId, e);
        }
        setEmps([...seen.values()].sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)));
      })
      .catch(() => { if (live) setEmps([]); });
    return () => { live = false; };
  }, [locs, anchor]);

  const ts = profile.timesheets;
  const setName = (id, v) => patch({ timesheets: { ...ts, qbNames: { ...ts.qbNames, [id]: v } } });
  const setFile = (id, v) => patch({ timesheets: { ...ts, adp: { ...ts.adp, fileNumbers: { ...ts.adp.fileNumbers, [id]: v } } } });

  if (emps === null) return <Spinner label="Looking up employees…" />;
  if (emps.length === 0) return <Empty>No shifts found for these stores in the last 90 days.</Empty>;
  return (
    <div className="table-wrap">
      <table className="data admin-table">
        <thead>
          <tr>
            <th className="no-sort">Employee</th>
            <th className="no-sort">Name in QuickBooks</th>
            <th className="no-sort">ADP file #</th>
          </tr>
        </thead>
        <tbody>
          {emps.map(e => (
            <tr key={e.employeeId}>
              <td>
                {`${e.firstName || ''} ${e.lastName || ''}`.trim() || <span className="pill bad">name?</span>} <span className="hint">#{e.employeeId}{e.active === false ? ' · inactive' : ''}</span>
                {e.nameCandidates?.length > 0 && <div className="hint">no record from this store's terminals — other stores use this number for: {e.nameCandidates.join(', ')}</div>}
              </td>
              <td><input className="field" placeholder={`${e.firstName || ''} ${e.lastName || ''}`.trim() || 'Payroll name'} value={ts.qbNames?.[e.employeeId] || ''} onChange={ev => setName(e.employeeId, ev.target.value)} disabled={disabled} /></td>
              <td><input className="field" placeholder="File #" value={ts.adp?.fileNumbers?.[e.employeeId] || ''} onChange={ev => setFile(e.employeeId, ev.target.value)} disabled={disabled} style={{ width: 110 }} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProfileEditor({ profile, patch, storeOptions, claimed, defaults, anchor, disabled }) {
  const [showEmployees, setShowEmployees] = useState(false);
  const acc = profile.accounts;
  const opt = profile.options;
  const ts = profile.timesheets;
  const byType = acc.salesByOrderType;
  const storeName = (id) => storeOptions.find(o => o.id === id)?.label || `Store ${id}`;
  // stores already in another profile can't be picked here
  const options = storeOptions.filter(o => !claimed.has(o.id) || profile.locations.includes(o.id));

  return (
    <div className="admin-profile-body">
      <div className="admin-section">
        <div className="admin-section-head"><strong>Stores</strong></div>
        <MultiDropdown icon="🏪" allLabel="Choose stores…" options={options} selected={profile.locations}
          onChange={(locations) => patch({ locations })} />
        {profile.locations.length > 0 && (
          <div className="admin-grid2" style={{ marginTop: 8 }}>
            {profile.locations.map(id => (
              <label key={id} className="admin-field">
                <span>{storeName(id)} — {profile.software === 'qbo' ? 'QuickBooks Location' : 'Class'} name</span>
                <input className="field" placeholder={storeName(id)} value={profile.classes?.[id] || ''}
                  onChange={e => patch({ classes: { ...profile.classes, [id]: e.target.value } })} />
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="admin-section">
        <div className="admin-section-head"><strong>Accounts</strong></div>
        <div className="hint">{nameHint(profile.software)}</div>
        <div className="admin-grid2">
          {ACCOUNT_LABELS.map(([key, label]) => (
            <label key={key} className="admin-field">
              <span>{label}</span>
              <input className="field" value={acc[key] || ''} placeholder={defaults?.accounts?.[key] || ''}
                onChange={e => patch({ accounts: { ...acc, [key]: e.target.value } })} />
            </label>
          ))}
        </div>
        <label className="admin-check" style={{ marginTop: 8 }}>
          <input type="checkbox" checked={!!byType}
            onChange={e => patch({ accounts: { ...acc, salesByOrderType: e.target.checked ? Object.fromEntries(ORDER_TYPES.map(t => [t, ''])) : null } })} />
          Split sales income by order type
        </label>
        {byType && (
          <div className="admin-grid2">
            {ORDER_TYPES.map(t => (
              <label key={t} className="admin-field">
                <span>{t} sales</span>
                <input className="field" value={byType[t] || ''} placeholder={acc.sales}
                  onChange={e => patch({ accounts: { ...acc, salesByOrderType: { ...byType, [t]: e.target.value } } })} />
              </label>
            ))}
          </div>
        )}
      </div>

      <TenderMap profile={profile} patch={patch} defaults={defaults?.tenders} anchor={anchor} disabled={disabled} />

      <div className="admin-section">
        <div className="admin-section-head"><strong>Journal options</strong></div>
        <div className="admin-grid2">
          <label className="admin-field">
            <span>One entry per</span>
            <select className="field" value={opt.granularity} onChange={e => patch({ options: { ...opt, granularity: e.target.value } })}>
              <option value="month">store per month</option>
              <option value="day">store per business day</option>
            </select>
          </label>
          <label className="admin-field">
            <span>Journal number prefix</span>
            <input className="field" value={opt.journalPrefix || ''} placeholder="SJ" onChange={e => patch({ options: { ...opt, journalPrefix: e.target.value } })} />
          </label>
          <label className="admin-field" style={{ gridColumn: '1 / -1' }}>
            <span>Memo</span>
            <input className="field" value={opt.memoTemplate || ''} placeholder="{store} sales {period}" onChange={e => patch({ options: { ...opt, memoTemplate: e.target.value } })} />
          </label>
        </div>
        <label className="admin-check"><input type="checkbox" checked={!!opt.grossDiscounts} onChange={e => patch({ options: { ...opt, grossDiscounts: e.target.checked } })} /> Book gross sales with a separate discounts line (default: net sales only)</label>
        <label className="admin-check"><input type="checkbox" checked={!!opt.deliverySeparate} onChange={e => patch({ options: { ...opt, deliverySeparate: e.target.checked } })} /> Book delivery charges to their own income account</label>
        <label className="admin-check"><input type="checkbox" checked={!!opt.includeCosts} onChange={e => patch({ options: { ...opt, includeCosts: e.target.checked } })} /> Include costs entered at the POS as expense lines (paid from cash)</label>
      </div>

      <div className="admin-section">
        <div className="admin-section-head"><strong>Timesheets &amp; payroll</strong></div>
        <div className="admin-grid2">
          <label className="admin-field">
            <span>Pay period</span>
            <select className="field" value={profile.payPeriod.type} onChange={e => patch({ payPeriod: { ...profile.payPeriod, type: e.target.value } })}>
              {PAY_PERIODS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>
          <label className="admin-field">
            <span>A period starts on</span>
            <input className="field" type="date" value={profile.payPeriod.anchorDate || ''} onChange={e => patch({ payPeriod: { ...profile.payPeriod, anchorDate: e.target.value } })} />
          </label>
          <label className="admin-field">
            <span>Overtime after (hours / week, blank = none)</span>
            <input className="field" type="number" min="1" max="168" value={ts.overtimeWeeklyHours ?? ''} onChange={e => patch({ timesheets: { ...ts, overtimeWeeklyHours: e.target.value === '' ? null : Number(e.target.value) } })} />
          </label>
          <label className="admin-field">
            <span>ADP company code</span>
            <input className="field" value={ts.adp?.coCode || ''} onChange={e => patch({ timesheets: { ...ts, adp: { ...ts.adp, coCode: e.target.value } } })} />
          </label>
          <label className="admin-field">
            <span>ADP batch id</span>
            <input className="field" value={ts.adp?.batchId || ''} onChange={e => patch({ timesheets: { ...ts, adp: { ...ts.adp, batchId: e.target.value } } })} />
          </label>
        </div>
        <label className="admin-check"><input type="checkbox" checked={!!ts.showPay} onChange={e => patch({ timesheets: { ...ts, showPay: e.target.checked } })} /> Show pay earned on timesheets (hours × POS hourly wage; suspicious wages are flagged, not multiplied)</label>
        <div style={{ marginTop: 8 }}>
          <button className="btn-ghost" onClick={() => setShowEmployees(s => !s)}>{showEmployees ? 'Hide' : 'Map'} employee payroll names / ADP file numbers</button>
        </div>
        {showEmployees && <EmployeeMap profile={profile} patch={patch} anchor={anchor} disabled={disabled} />}
      </div>
    </div>
  );
}

export default function AccountingProfilesCard({ me }) {
  const storeOptions = useStoreOptions();
  const { anchor } = useFilters();
  const s = useEditableList('/api/admin/accounting-profiles', d => d.profiles, items => ({ accountingProfiles: items }));
  const [openId, setOpenId] = useState(null);

  if (s.loadError) return <ErrorBox message={s.loadError} />;
  if (!s.meta) return <Spinner label="Loading accounting profiles…" />;

  const profiles = s.items;
  const defaults = s.meta.default;
  const disabled = !s.meta.editable || s.saving;
  const claimed = new Map(profiles.flatMap((p, i) => p.locations.map(l => [l, i])));
  const unassigned = storeOptions.filter(o => !claimed.has(o.id));
  const setProfile = (i, patch) => s.setItems(ps => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const addProfile = () => {
    s.setItems(ps => [...ps, { ...blankProfile(defaults), name: `Profile ${ps.length + 1}` }]);
    setOpenId(profiles.length);
  };

  return (
    <>
      {!s.meta.editable && (
        <div className="notice"><strong>Read-only:</strong> {s.meta.reason}</div>
      )}
      <fieldset className="bare" disabled={disabled}>
        <Card
          tour="admin-accounting"
          title="Accounting Profiles"
          sub={me?.role === 'admin'
            ? 'how each store set’s sales journal and timesheets map to its accounting software · stores in no profile use the built-in default'
            : 'how your stores’ sales journal and timesheets map to your accounting software · profiles for other stores are managed by head office'}
        >
          {profiles.length === 0 && (
            <Empty>No profiles yet — every store exports with the built-in default account names and generic CSV. Add a profile to pick your software and map accounts.</Empty>
          )}
          {profiles.map((p, i) => {
            const open = openId === i;
            const unmappedHint = Object.values(p.tenders || {}).some(v => !v);
            return (
              <div className={`admin-profile ${open ? 'open' : ''}`} key={p.id || `new-${i}`}>
                <div className="admin-addrow">
                  <button className="btn-ghost" title={open ? 'Collapse' : 'Expand'} onClick={() => setOpenId(open ? null : i)}>{open ? '▾' : '▸'}</button>
                  <input className="field" placeholder="Profile name (e.g. Head office, Westside stores)" value={p.name}
                    onChange={e => setProfile(i, { name: e.target.value })} style={{ minWidth: 220 }} />
                  <select className="field" value={p.software} onChange={e => setProfile(i, { software: e.target.value })}>
                    {SOFTWARE.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                  <span className="hint">
                    {p.locations.length === 0 ? 'no stores yet' : p.locations.map(id => storeOptions.find(o => o.id === id)?.label || id).join(', ')}
                  </span>
                  {unmappedHint && <span className="pill bad">unmapped tenders</span>}
                  <button className="btn-ghost" title="Remove profile" onClick={() => { s.setItems(ps => ps.filter((_, j) => j !== i)); setOpenId(null); }}>✕</button>
                </div>
                {open && (
                  <ProfileEditor profile={p} patch={patch => setProfile(i, patch)} storeOptions={storeOptions}
                    claimed={new Map([...claimed].filter(([, j]) => j !== i))} defaults={defaults} anchor={anchor} disabled={disabled} />
                )}
              </div>
            );
          })}
          {unassigned.length > 0 && profiles.length > 0 && (
            <div className="hint" style={{ marginTop: 8 }}>
              Using the built-in default: {unassigned.map(o => o.label).join(', ')}.
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <button className="btn-ghost" onClick={addProfile}>+ Add profile</button>
          </div>

          <SaveBar dirty={s.dirty} saving={s.saving} savedFlash={s.savedFlash} onSave={s.save} onDiscard={s.discard} />
        </Card>
      </fieldset>
      {s.saveError && <ErrorBox message={s.saveError} />}
    </>
  );
}
